import React from 'react'
import { useGalaxyStore, useSelectedTransaction, useSelectedBlock, useSelectedMiningCollector } from '../../../core/state/GalaxyStore'
import { formatBitcoin, formatSatsPerByte, formatTime, truncateHash } from '../../utils/formatters'
import './ObjectInspector.css'

export interface ObjectInspectorProps {
  className?: string
}

export const ObjectInspector: React.FC<ObjectInspectorProps> = ({ className = '' }) => {
  const selectedObjectId = useGalaxyStore(state => state.ui.selectedObject)
  const selectedTransaction = useSelectedTransaction()
  const selectedBlock = useSelectedBlock()
  const selectedMiningCollector = useSelectedMiningCollector()

  if (!selectedObjectId) {
    return (
      <div className={`object-inspector ${className} empty`}>
        <div className="inspector-placeholder">
          <span>🔍</span>
          <p>Click on a transaction ship, block planet, or mining collector to inspect details</p>
        </div>
      </div>
    )
  }

  if (selectedTransaction) {
    return (
      <div className={`object-inspector ${className} transaction`}>
        <div className="inspector-header">
          <h3>🚀 Transaction Ship</h3>
          <button 
            className="close-btn"
            onClick={() => useGalaxyStore.getState().selectObject(null)}
            aria-label="Close inspector"
          >
            ×
          </button>
        </div>

        <div className="inspector-content">
          <div className="detail-group">
            <span className="detail-label">Transaction ID</span>
            <div className="detail-value hash-value">
              <span>{truncateHash(selectedTransaction.id, 12)}</span>
              <button 
                className="copy-btn"
                onClick={() => navigator.clipboard?.writeText(selectedTransaction.id)}
                title="Copy transaction ID"
              >
                📋
              </button>
            </div>
          </div>

          <div className="detail-group">
            <span className="detail-label">Amount</span>
            <span className="detail-value amount">
              {formatBitcoin(selectedTransaction.amount)}
            </span>
          </div>

          <div className="detail-group">
            <span className="detail-label">Fee Rate</span>
            <span className="detail-value fee-rate">
              {formatSatsPerByte(selectedTransaction.feeRate)}
            </span>
          </div>

          <div className="detail-group">
            <span className="detail-label">Status</span>
            <span className={`detail-value status ${selectedTransaction.status === 'confirmed' ? 'confirmed' : 'pending'}`}>
              {selectedTransaction.status === 'confirmed' ? '✅ Confirmed' : '⏳ Pending'}
            </span>
          </div>

          {selectedTransaction.status === 'confirmed' && (
            <div className="detail-group">
              <span className="detail-label">Block Height</span>
              <span className="detail-value block-height">
                #{selectedTransaction.blockHeight?.toLocaleString()}
              </span>
            </div>
          )}

          <div className="detail-group">
            <span className="detail-label">Ship Speed</span>
            <span className="detail-value speed">
              {Math.round(selectedTransaction.feeRate * 10)} km/h
            </span>
          </div>

          <div className="detail-group">
            <span className="detail-label">Flight Time</span>
            <span className="detail-value flight-time">
              {formatTime((Date.now() - selectedTransaction.spawnedAt) / 1000)}
            </span>
          </div>
        </div>
      </div>
    )
  }

  if (selectedBlock) {
    return (
      <div className={`object-inspector ${className} block`}>
        <div className="inspector-header">
          <h3>🪐 Block Planet</h3>
          <button 
            className="close-btn"
            onClick={() => useGalaxyStore.getState().selectObject(null)}
            aria-label="Close inspector"
          >
            ×
          </button>
        </div>

        <div className="inspector-content">
          <div className="detail-group">
            <span className="detail-label">Block Height</span>
            <span className="detail-value height">
              #{selectedBlock.height.toLocaleString()}
            </span>
          </div>

          <div className="detail-group">
            <span className="detail-label">Block Hash</span>
            <div className="detail-value hash-value">
              <span>{truncateHash(selectedBlock.hash, 12)}</span>
              <button 
                className="copy-btn"
                onClick={() => navigator.clipboard?.writeText(selectedBlock.hash)}
                title="Copy block hash"
              >
                📋
              </button>
            </div>
          </div>

          <div className="detail-group">
            <span className="detail-label">Transactions</span>
            <span className="detail-value tx-count">
              {selectedBlock.transactionCount.toLocaleString()}
            </span>
          </div>

          <div className="detail-group">
            <span className="detail-label">Total Fees</span>
            <span className="detail-value total-fees">
              {formatBitcoin(selectedBlock.totalFees)}
            </span>
          </div>

          <div className="detail-group">
            <span className="detail-label">Block Size</span>
            <span className="detail-value size">
              {(selectedBlock.size / 1024 / 1024).toFixed(2)} MB
            </span>
          </div>

          <div className="detail-group">
            <span className="detail-label">Mined Time</span>
            <span className="detail-value timestamp">
              {new Date(selectedBlock.timestamp).toLocaleString()}
            </span>
          </div>

          <div className="detail-group">
            <span className="detail-label">Planet Radius</span>
            <span className="detail-value radius">
              {Math.round(Math.log(selectedBlock.transactionCount + 1) * 5)} km
            </span>
          </div>
        </div>
      </div>
    )
  }

  if (selectedMiningCollector) {
    // For mining collectors, we need to get the data from the userData since they're not in store
    // This is a placeholder - we'll need to access the actual collector data
    const collectorId = selectedMiningCollector.replace('collector-', '');
    
    return (
      <div className={`object-inspector ${className} mining-collector`}>
        <div className="inspector-header">
          <h3>⛏️ Mining Collector</h3>
          <button 
            className="close-btn"
            onClick={() => useGalaxyStore.getState().selectObject(null)}
            aria-label="Close inspector"
          >
            ×
          </button>
        </div>

        <div className="inspector-content">
          <div className="detail-group">
            <span className="detail-label">Mining Pool</span>
            <span className="detail-value pool-name">
              {collectorId}
            </span>
          </div>

          <div className="detail-group">
            <span className="detail-label">Collector ID</span>
            <span className="detail-value collector-id">
              {selectedMiningCollector}
            </span>
          </div>

          <div className="detail-group">
            <span className="detail-label">Status</span>
            <span className="detail-value status operational">
              🟢 Operational
            </span>
          </div>

          <div className="detail-group">
            <span className="detail-label">Mining Phase</span>
            <span className="detail-value phase">
              Phase 3: Transaction Selection
            </span>
          </div>

          <div className="detail-group">
            <span className="detail-label">Visual Confirmed</span>
            <span className="detail-value confirmed">
              ✅ Mining platform visible and interactive
            </span>
          </div>

          <div className="detail-group">
            <span className="detail-label">Position</span>
            <span className="detail-value position">
              Orbiting mempool at extraction range
            </span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`object-inspector ${className} unknown`}>
      <div className="inspector-header">
        <h3>❓ Unknown Object</h3>
        <button 
          className="close-btn"
          onClick={() => useGalaxyStore.getState().selectObject(null)}
          aria-label="Close inspector"
        >
          ×
        </button>
      </div>
      <div className="inspector-content">
        <p>Selected object type not recognized: {selectedObjectId}</p>
      </div>
    </div>
  )
}